import { ColumnDef } from "@tanstack/react-table";
import { Fragment, ReactNode } from "react";
import { Employee, EmployeeRole } from "@/types/v2";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { CheckIcon, ClockIcon, XCircleIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import {
  EmployeeAccountDisplayState,
  getEmployeeAccountDisplayState,
  getServiceAdvisorColor,
} from "@/lib/employees";
import { Avatar } from "@/components/Common/Avatar";
import { Badge } from "@/components/Base/Badge";
import { cn } from "@/lib/utils";
import { JOB_TITLES } from "@/lib/const";

export type DatatableEmployee = Employee & {
  authAccountStatus: "inactive" | "active" | "doesNotExist";
  hasLoggedIn: boolean;
};

interface ColumnProps {
  actions: (employee: DatatableEmployee) => ReactNode[];
  loading?: boolean;
  onVisibilityChange: (id: string, visible: boolean) => void;
  onActivateAccount: (id: string) => void;
  /** Activate on an existing, disabled account: asks whether to reset the password. */
  onReactivateAccount: (id: string) => void;
  onDeactivateAccount: (id: string) => void;
  onResendActivationEmail: (id: string) => void;
  onRoleChange: (id: string, role: EmployeeRole) => void;
  terminolizeAuto: (arg: string) => string;
}

const createActionsColumn = ({
  actions,
}: ColumnProps): ColumnDef<DatatableEmployee> => ({
  accessorKey: "actions",
  header: "",
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="flex gap-2">
        {actions(item).map((action, index) => (
          <Fragment key={`action-${item.id}-${index}`}>{action}</Fragment>
        ))}
      </div>
    );
  },
});

const getAccountStatusIconAndLabel = (
  state: EmployeeAccountDisplayState
): { icon: ReactNode; label: string } => {
  switch (state.kind) {
    case "doesNotExist":
      return { icon: <XCircleIcon />, label: "Does Not Exist" };
    case "inactive":
      return { icon: <XCircleIcon />, label: "Disabled" };
    case "notAssociated":
      return { icon: <XCircleIcon />, label: "Not Enabled for This Shop" };
    case "pendingFirstLogin":
      if (state.emailStatus === "delivered") {
        return { icon: <CheckIcon />, label: "Invite Email Sent" };
      }
      if (state.emailStatus === "rejected") {
        return { icon: <XCircleIcon />, label: "Invite Email Rejected" };
      }
      return { icon: <ClockIcon />, label: "Invite Email Pending" };
    case "active":
      return { icon: <CheckIcon />, label: "Active" };
  }
};

const renderAccountStatusActions = (
  state: EmployeeAccountDisplayState,
  item: DatatableEmployee,
  props?: ColumnProps
): ReactNode => {
  switch (state.kind) {
    case "doesNotExist":
      return (
        <Button
          id="employee_account_button"
          loading={props?.loading}
          variant="default"
          onClick={() => props?.onActivateAccount(item.id)}
          className="w-32"
        >
          Create & Activate
        </Button>
      );
    case "inactive":
      return (
        <Button
          id="employee_account_button"
          loading={props?.loading}
          variant="default"
          onClick={() => props?.onReactivateAccount(item.id)}
          className="w-32"
        >
          Activate
        </Button>
      );
    case "notAssociated":
      return (
        <Button
          id="employee_account_button"
          loading={props?.loading}
          variant="default"
          onClick={() => props?.onRoleChange(item.id, "viewOnly")}
          className="w-32"
        >
          Activate for Shop
        </Button>
      );
    case "pendingFirstLogin":
      return (
        <>
          <Button
            id="employee_account_resend_button"
            loading={props?.loading}
            variant="outline"
            onClick={() => props?.onResendActivationEmail(item.id)}
            className="w-32"
          >
            Resend
          </Button>
          <Button
            id="employee_account_button"
            loading={props?.loading}
            variant="destructive"
            onClick={() => props?.onDeactivateAccount(item.id)}
            className="w-32"
          >
            Deactivate
          </Button>
        </>
      );
    case "active":
      return (
        <Button
          id="employee_account_button"
          loading={props?.loading}
          variant="destructive"
          onClick={() => props?.onDeactivateAccount(item.id)}
          className="w-32"
        >
          Deactivate
        </Button>
      );
  }
};

const employeeColumns = (props?: ColumnProps) => {
  const col: ColumnDef<DatatableEmployee>[] = [
    {
      accessorKey: "id",
      header: "ID",
    },
    {
      accessorKey: "firstName",
      header: ({ column }) => <HeaderSortable column={column} title={"Name"} />,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className={cn(item._deleted && "opacity-60")}>
            <div className="flex items-center gap-2">
              <div
                className="text-base"
                id="employee_name"
              >{`${item.firstName} ${item.lastName}`}</div>
              {item._deleted && <Badge variant="secondary">Deleted</Badge>}
            </div>
            <div className="text-sm" id="employee_email">
              {item.email}
            </div>
            <div className="text-sm text-muted" id="employee_role">
              {item.integrationRole}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "color",
      header: ({ column }) => (
        <HeaderSortable column={column} title={"Color"} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        const color = getServiceAdvisorColor(item.id, [item]);
        return (
          <div className="flex items-center gap-2">
            <Avatar
              bgColor={color}
              className={"h-8 w-8 border border-primary-foreground"}
            />
            <span>{color}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "hideInShopHero",
      header: ({ column }) => (
        <HeaderSortable column={column} title={"Visibility"} />
      ),
      sortingFn: (rowA) => {
        if (rowA.original.hideInShopHero === true) {
          return -1;
        }
        return 1;
      },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Select
            onValueChange={(value) =>
              props?.onVisibilityChange(item.id, value === "hidden")
            }
            value={item.hideInShopHero ? "hidden" : "visible"}
            disabled={item._deleted}
          >
            <SelectTrigger
              className={"w-48 text-primary"}
              id="employee_visibility"
            >
              <SelectValue placeholder="Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hidden">Hidden in Shop Hero</SelectItem>
              <SelectItem value="visible">Visible in Shop Hero</SelectItem>
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "authAccountStatus",
      header: ({ column }) => (
        <HeaderSortable column={column} title={"Shop Hero Account"} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        const displayState = getEmployeeAccountDisplayState(item);
        const { icon, label } = getAccountStatusIconAndLabel(displayState);

        return (
          <div
            className="flex flex-row items-center gap-2"
            id="employee_account"
          >
            {icon}
            {label}
            <div className={"flex-grow"} />
            {!item._deleted &&
              renderAccountStatusActions(displayState, item, props)}
          </div>
        );
      },
    },
    {
      accessorKey: "shopHeroRole",
      header: ({ column }) => (
        <HeaderSortable column={column} title={"Employee Role"} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        if (
          item._deleted ||
          item.authAccountStatus !== "active" ||
          !item.shopHeroAuthUid
        ) {
          return null;
        }
        return (
          <Select
            onValueChange={(value: EmployeeRole) =>
              props?.onRoleChange(item.id, value)
            }
            value={item.shopHeroRole ?? "admin"} // Default to admin because behind the scenes we do.
          >
            <SelectTrigger
              className={"w-48 text-primary"}
              loading={props?.loading}
              id="employee_role"
            >
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {EmployeeRole.map((item) => (
                <SelectItem value={item} key={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },

    {
      accessorKey: "shopHeroJobTitle",
      header: ({ column }) => (
        <HeaderSortable column={column} title={"Job Title"} />
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div>
            <div
              className="text-base"
              id="job_title"
            >{`${props?.terminolizeAuto ? props.terminolizeAuto(JOB_TITLES[item.shopHeroJobTitle ?? "other"]) : JOB_TITLES[item.shopHeroJobTitle ?? "other"]}`}</div>
          </div>
        );
      },
    },
  ];

  if (props) {
    col.push(createActionsColumn(props));
  }
  return col;
};

export { employeeColumns };
